// Push Notification Service for Riddl PWA
// Based on best practices from Educative.io guide

export interface PushSubscription {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export interface PushNotificationSettings {
  enabled: boolean;
  morningTime: string;
  afternoonTime: string;
  eveningTime: string;
  serverUrl: string;
}

const DEFAULT_PUSH_SETTINGS: PushNotificationSettings = {
  enabled: false,
  morningTime: '09:00',
  afternoonTime: '14:00',
  eveningTime: '18:00',
  serverUrl: window.location.hostname === 'localhost' 
    ? 'http://localhost:3001' 
    : 'https://riddle-server-production.up.railway.app'
};

const STORAGE_KEY = 'riddle-v2:push-settings';
const SUBSCRIPTION_KEY = 'riddle-v2:push-subscription';

// Check if push notifications are supported
export const isPushSupported = (): boolean => {
  const hasServiceWorker = 'serviceWorker' in navigator;
  const hasPushManager = 'PushManager' in window;
  const hasNotification = 'Notification' in window;
  
  console.log('🔍 Push notification support check:', {
    hasServiceWorker,
    hasPushManager,
    hasNotification,
    userAgent: navigator.userAgent,
    isSecureContext: window.isSecureContext
  });
  
  return hasServiceWorker && hasPushManager && hasNotification;
};

// Check current notification permission
export const getNotificationPermission = (): NotificationPermission => {
  if (!('Notification' in window)) {
    return 'denied';
  }
  return Notification.permission;
};

// Request notification permission (soft opt-in approach)
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    console.log('Notifications not supported');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    console.log('User has blocked push notifications');
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
};

// Save push notification settings
export const savePushSettings = (settings: PushNotificationSettings): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to save push settings:', error);
  }
};

// Load push notification settings
export const loadPushSettings = (): PushNotificationSettings => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_PUSH_SETTINGS, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.error('Failed to load push settings:', error);
  }
  return DEFAULT_PUSH_SETTINGS;
};

// Save push subscription
export const savePushSubscription = (subscription: PushSubscription | null): void => {
  try {
    if (subscription) {
      localStorage.setItem(SUBSCRIPTION_KEY, JSON.stringify(subscription));
    } else {
      localStorage.removeItem(SUBSCRIPTION_KEY);
    }
  } catch (error) {
    console.error('Failed to save push subscription:', error);
  }
};

// Load push subscription
export const loadPushSubscription = (): PushSubscription | null => {
  try {
    const stored = localStorage.getItem(SUBSCRIPTION_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Failed to load push subscription:', error);
    return null;
  }
};

// Subscribe to push notifications
export const subscribeToPush = async (): Promise<PushSubscription | null> => {
  console.log('🚀 Starting push notification subscription process...');
  
  if (!isPushSupported()) {
    console.log('❌ Push notifications not supported');
    return null;
  }

  console.log('✅ Push notifications supported, requesting permission...');
  const permission = await requestNotificationPermission();
  console.log('📱 Permission result:', permission);
  
  if (permission !== 'granted') {
    console.log('❌ Notification permission not granted:', permission);
    return null;
  }

  try {
    console.log('🔧 Getting service worker registration...');
    const registration = await navigator.serviceWorker.ready;
    console.log('✅ Service worker ready:', registration);
    
    // Check if we already have a subscription
    let subscription = await registration.pushManager.getSubscription();
    console.log('🔍 Existing subscription:', subscription ? 'Found' : 'None');
    
    if (!subscription) {
      console.log('🆕 Creating new push subscription...');
      // Create new subscription
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true, // Required for web push
        applicationServerKey: urlBase64ToUint8Array(getVapidPublicKey())
      });
      console.log('✅ New subscription created:', subscription);
    }

    // Save subscription locally
    const subscriptionData: PushSubscription = {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: arrayBufferToBase64(subscription.getKey('p256dh')!),
        auth: arrayBufferToBase64(subscription.getKey('auth')!)
      }
    };

    savePushSubscription(subscriptionData);

    // Send subscription to server
    await sendSubscriptionToServer(subscriptionData);

    console.log('Successfully subscribed to push notifications');
    return subscriptionData;
  } catch (error) {
    console.error('Failed to subscribe to push notifications:', error);
    return null;
  }
};

// Unsubscribe from push notifications
export const unsubscribeFromPush = async (): Promise<boolean> => {
  if (!isPushSupported()) {
    console.log('Push notifications not supported');
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      savePushSubscription(null);
      
      // Notify server to remove subscription
      await removeSubscriptionFromServer();
      
      console.log('Successfully unsubscribed from push notifications');
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Failed to unsubscribe from push notifications:', error);
    return false;
  }
};

// Send subscription to server
const sendSubscriptionToServer = async (subscription: PushSubscription): Promise<void> => {
  const settings = loadPushSettings();
  
  try {
    const response = await fetch(`${settings.serverUrl}/api/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        subscription,
        settings: {
          morningTime: settings.morningTime,
          afternoonTime: settings.afternoonTime,
          eveningTime: settings.eveningTime
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Server responded with ${response.status}`);
    }

    console.log('Subscription sent to server successfully');
  } catch (error) {
    console.error('Failed to send subscription to server:', error);
    // Don't throw error - subscription is still valid locally
  }
};

// Remove subscription from server
const removeSubscriptionFromServer = async (): Promise<void> => {
  const settings = loadPushSettings();
  const subscription = loadPushSubscription();
  
  if (!subscription) return;

  try {
    const response = await fetch(`${settings.serverUrl}/api/push/unsubscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ subscription })
    });

    if (!response.ok) {
      throw new Error(`Server responded with ${response.status}`);
    }

    console.log('Subscription removed from server successfully');
  } catch (error) {
    console.error('Failed to remove subscription from server:', error);
  }
};

// Update notification settings on server
export const updateNotificationSettings = async (settings: PushNotificationSettings): Promise<void> => {
  const subscription = loadPushSubscription();
  
  if (!subscription) {
    console.log('No push subscription found');
    return;
  }

  try {
    const response = await fetch(`${settings.serverUrl}/api/push/settings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        subscription,
        settings: {
          morningTime: settings.morningTime,
          afternoonTime: settings.afternoonTime,
          eveningTime: settings.eveningTime
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Server responded with ${response.status}`);
    }

    console.log('Notification settings updated on server');
  } catch (error) {
    console.error('Failed to update notification settings:', error);
  }
};

// Test push notification
export const testPushNotification = async (): Promise<void> => {
  const subscription = loadPushSubscription();
  
  if (!subscription) {
    console.log('No push subscription found');
    return;
  }

  const settings = loadPushSettings();
  
  try {
    const response = await fetch(`${settings.serverUrl}/api/push/test`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ subscription })
    });

    if (!response.ok) {
      throw new Error(`Server responded with ${response.status}`);
    }

    console.log('Test push notification sent');
  } catch (error) {
    console.error('Failed to send test push notification:', error);
  }
};

// Force notification from server to all subscribers
export const forcePushNotification = async (title?: string, body?: string): Promise<void> => {
  const settings = loadPushSettings();
  
  try {
    const response = await fetch(`${settings.serverUrl}/api/push/force`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ title, body })
    });

    if (!response.ok) {
      throw new Error(`Server responded with ${response.status}`);
    }

    const result = await response.json();
    console.log('Force notification result:', result);
    return result;
  } catch (error) {
    console.error('Failed to send force push notification:', error);
    throw error;
  }
};

// Start auto-notifications every 10 minutes
export const startAutoNotifications = async (): Promise<void> => {
  const settings = loadPushSettings();
  
  try {
    const response = await fetch(`${settings.serverUrl}/api/push/auto-start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`Server responded with ${response.status}`);
    }

    const result = await response.json();
    console.log('Auto-notifications started:', result);
    return result;
  } catch (error) {
    console.error('Failed to start auto-notifications:', error);
    throw error;
  }
};

// Stop auto-notifications
export const stopAutoNotifications = async (): Promise<void> => {
  const settings = loadPushSettings();
  
  try {
    const response = await fetch(`${settings.serverUrl}/api/push/auto-stop`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`Server responded with ${response.status}`);
    }

    const result = await response.json();
    console.log('Auto-notifications stopped:', result);
    return result;
  } catch (error) {
    console.error('Failed to stop auto-notifications:', error);
    throw error;
  }
};

// Get auto-notification status
export const getAutoNotificationStatus = async (): Promise<{running: boolean, interval: string | null}> => {
  const settings = loadPushSettings();
  
  try {
    const response = await fetch(`${settings.serverUrl}/api/push/auto-status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`Server responded with ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Failed to get auto-notification status:', error);
    return { running: false, interval: null };
  }
};

// Utility functions
const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

// VAPID public key
const getVapidPublicKey = (): string => {
  return 'BHS8VhK1ZSYGcvOk_EnykBAbCUqpEQPZh4FObJ1gGmgXScwAv_CYdF7gM8X6oN0ta98D5cs54IS80qLx2zL1GK8';
};

// Debug function to check push notification state
export const debugPushNotificationState = async (): Promise<void> => {
  console.log('🔍 === PUSH NOTIFICATION DEBUG INFO ===');
  console.log('📱 User Agent:', navigator.userAgent);
  console.log('🔒 Secure Context:', window.isSecureContext);
  console.log('🌐 Protocol:', window.location.protocol);
  console.log('🏠 Hostname:', window.location.hostname);
  
  console.log('🔧 Browser Support:');
  console.log('  - Service Worker:', 'serviceWorker' in navigator);
  console.log('  - Push Manager:', 'PushManager' in window);
  console.log('  - Notification:', 'Notification' in window);
  
  console.log('📋 Current Permission:', Notification.permission);
  
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      console.log('🔧 Service Worker Registration:', registration ? 'Found' : 'None');
      
      if (registration) {
        console.log('📱 Service Worker Scope:', registration.scope);
        console.log('📱 Service Worker State:', registration.active?.state);
        
        if (registration.pushManager) {
          const subscription = await registration.pushManager.getSubscription();
          console.log('🔔 Push Subscription:', subscription ? 'Active' : 'None');
        }
      }
    } catch (error) {
      console.error('❌ Error checking service worker:', error);
    }
  }
  
  console.log('🔍 === END DEBUG INFO ===');
};

// Check if user is subscribed
export const isSubscribed = async (): Promise<boolean> => {
  if (!isPushSupported()) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch (error) {
    console.error('Failed to check subscription status:', error);
    return false;
  }
};
